import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  LogOut, 
  RefreshCw,
  Phone,
  MapPin,
  Clock,
  DollarSign,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  MessageSquare,
  Calendar,
  User,
  Download,
  FileText
} from 'lucide-react';
import TherapistChat from '../components/TherapistChat';
import axios from 'axios';
import { POLL_INTERVAL_MS } from '../constants';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TherapistPortal = () => {
  const navigate = useNavigate();
  const { user, token, logout, isTherapist } = useAuth();
  const { t } = useLanguage();
  
  const [profile, setProfile] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingAvailability, setUpdatingAvailability] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [chatBooking, setChatBooking] = useState(null);
  const [savingHours, setSavingHours] = useState(false);
  const [workStart, setWorkStart] = useState('09:00');
  const [workEnd, setWorkEnd] = useState('21:00');

  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [profileRes, bookingsRes] = await Promise.all([
        axios.get(`${API_URL}/api/therapist/me`, { headers }),
        axios.get(`${API_URL}/api/therapist/bookings`, { headers })
      ]);
      setProfile(profileRes.data);
      setBookings(bookingsRes.data);
      setWorkStart(profileRes.data.working_hours_start || '09:00');
      setWorkEnd(profileRes.data.working_hours_end || '21:00');
    } catch (error) {
      if (error.response?.status === 401) {
        logout();
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  }, [token, logout, navigate]);

  useEffect(() => {
    if (!isTherapist) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [isTherapist, navigate, fetchData]);

  // Poll for new bookings every 30 seconds
  useEffect(() => {
    if (!isTherapist) return;
    const interval = setInterval(fetchData, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isTherapist, fetchData]);

  const toggleAvailability = async () => {
    setUpdatingAvailability(true);
    try {
      const response = await axios.put(
        `${API_URL}/api/therapist/availability`,
        {},
        { headers: authHeaders }
      );
      setProfile(prev => ({ ...prev, availability_status: response.data.availability_status }));
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update availability');
    } finally {
      setUpdatingAvailability(false);
    }
  };

  const saveWorkingHours = async () => {
    setSavingHours(true);
    try {
      const response = await axios.put(
        `${API_URL}/api/therapist/working-hours`,
        { working_hours_start: workStart, working_hours_end: workEnd },
        { headers: authHeaders }
      );
      setProfile(response.data);
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to update working hours');
    } finally {
      setSavingHours(false);
    }
  };

  const handleAcceptBooking = async (bookingId) => {
    setActionLoading(prev => ({ ...prev, [bookingId]: 'accept' }));
    try {
      await axios.put(
        `${API_URL}/api/therapist/bookings/${bookingId}/accept`,
        {},
        { headers: authHeaders }
      );
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to accept booking');
    } finally {
      setActionLoading(prev => ({ ...prev, [bookingId]: null }));
    }
  };

  const handleCompleteBooking = async (bookingId) => {
    setActionLoading(prev => ({ ...prev, [bookingId]: 'complete' }));
    try {
      await axios.put(
        `${API_URL}/api/therapist/bookings/${bookingId}/complete`,
        {},
        { headers: authHeaders }
      );
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to complete booking');
    } finally {
      setActionLoading(prev => ({ ...prev, [bookingId]: null }));
    }
  };

  const getStatusBadge = (booking) => {
    if (booking.therapist_completed) {
      return <Badge className="bg-green-600">Completed</Badge>;
    }
    if (booking.therapist_accepted) {
      return <Badge className="bg-blue-600">Accepted</Badge>;
    }
    return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">Pending Acceptance</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const activeBookings = bookings.filter(b => !b.therapist_completed && b.status !== 'cancelled');
  const completedBookings = bookings.filter(b => b.therapist_completed || b.status === 'completed');

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-pink-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-3 sm:px-4 py-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <img 
                src="https://customer-assets.emergentagent.com/job_spa-bookings-5/artifacts/syk2guux_Angel-Massage-logo-png.png" 
                alt="Angel Massage"
                className="h-8 sm:h-10 w-auto shrink-0"
              />
              <div className="min-w-0">
                <h1 className="text-sm sm:text-lg font-semibold text-gray-800 truncate">{t('therapist.title')}</h1>
                <p className="text-xs text-gray-500 hidden sm:block">{t('therapist.manageBookings')}</p>
              </div>
            </div>
            <div className="flex items-center gap-1 sm:gap-3 shrink-0">
              <LanguageSwitcher variant="outline" />
              <Button variant="outline" size="sm" onClick={fetchData} className="px-2 sm:px-3" title={t('common.refresh')}>
                <RefreshCw className="w-4 h-4" />
                <span className="hidden sm:inline ml-2">{t('common.refresh')}</span>
              </Button>
              <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/'); }} className="px-2 sm:px-3" title={t('common.logout')}>
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline ml-2">{t('common.logout')}</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
        {/* Profile Card */}
        <Card className="mb-6" data-testid="therapist-profile">
          <CardContent className="pt-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-pink-100 flex items-center justify-center shrink-0">
                  <User className="w-6 h-6 sm:w-8 sm:h-8 text-pink-600" />
                </div>
                <div>
                  <h2 className="text-lg sm:text-xl font-semibold">{profile?.name}</h2>
                  <p className="text-sm text-gray-500">{profile?.phone_number}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 ml-auto sm:ml-0">
                <span className={`text-sm font-medium ${profile?.availability_status === 'available' ? 'text-green-600' : 'text-gray-500'}`}>
                  {profile?.availability_status === 'available' ? t('common.available') : t('common.unavailable')}
                </span>
                <Switch
                  checked={profile?.availability_status === 'available'}
                  onCheckedChange={toggleAvailability}
                  disabled={updatingAvailability}
                  data-testid="availability-toggle"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Working Hours Card */}
        <Card className="mb-6" data-testid="working-hours-card">
          <CardContent className="pt-5">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-4 h-4 text-pink-600" />
              <h3 className="font-medium text-sm">Working Hours</h3>
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[120px]">
                <label className="text-xs text-gray-500 block mb-1">Start Time</label>
                <input
                  type="time"
                  value={workStart}
                  onChange={(e) => setWorkStart(e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-md border border-input bg-background"
                  data-testid="working-hours-start"
                />
              </div>
              <div className="flex-1 min-w-[120px]">
                <label className="text-xs text-gray-500 block mb-1">End Time</label>
                <input
                  type="time"
                  value={workEnd}
                  onChange={(e) => setWorkEnd(e.target.value)}
                  className="w-full px-3 py-2 text-sm rounded-md border border-input bg-background"
                  data-testid="working-hours-end"
                />
              </div>
              <Button
                size="sm"
                onClick={saveWorkingHours}
                disabled={savingHours || (workStart === (profile?.working_hours_start || '09:00') && workEnd === (profile?.working_hours_end || '21:00'))}
                data-testid="save-working-hours-btn"
              >
                {savingHours ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
              </Button>
            </div>
            <p className="text-xs text-gray-400 mt-2">
              Currently: {profile?.working_hours_start || '09:00'} - {profile?.working_hours_end || '21:00'}
            </p>
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6 text-center">
              <div className="text-3xl font-bold text-blue-600">{activeBookings.length}</div>
              <p className="text-sm text-gray-500">{t('therapist.activeBookings')}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6 text-center">
              <div className="text-3xl font-bold text-green-600">{completedBookings.length}</div>
              <p className="text-sm text-gray-500">{t('common.completed')}</p>
            </CardContent>
          </Card>
        </div>

        {/* Manual Downloads */}
        <Card className="mb-6">
          <CardContent className="pt-5">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-4 h-4 text-pink-600" />
              <h3 className="font-medium text-sm">{t('therapist.operatingManual') || 'Operating Manual'}</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              <a
                href={`${API_URL}/api/manuals/Angel_Massage_Therapist_Manual_EN.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                download
                data-testid="download-therapist-manual-en"
              >
                <Button variant="outline" size="sm">
                  <Download className="w-3 h-3 mr-1" />
                  Manual (EN)
                </Button>
              </a>
              <a
                href={`${API_URL}/api/manuals/Angel_Massage_Therapist_Manual_TH.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                download
                data-testid="download-therapist-manual-th"
              >
                <Button variant="outline" size="sm">
                  <Download className="w-3 h-3 mr-1" />
                  {t('therapist.manualTH') || 'คู่มือ (TH)'}
                </Button>
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Bookings */}
        <Tabs defaultValue="active">
          <TabsList className="mb-4">
            <TabsTrigger value="active" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Active ({activeBookings.length})
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              History ({completedBookings.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="active">
            <div className="space-y-4">
              {activeBookings.length === 0 ? (
                <Card>
                  <CardContent className="py-8 text-center text-gray-500">
                    No active bookings assigned to you
                  </CardContent>
                </Card>
              ) : (
                activeBookings.map((booking) => (
                  <Card key={booking.id} data-testid={`booking-${booking.id}`}>
                    <CardContent className="pt-6">
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="font-semibold text-lg">{booking.name}</h3>
                          <p className="text-sm text-gray-500">{formatDate(booking.created_at)}</p>
                        </div>
                        {getStatusBadge(booking)}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm mb-4">
                        <div className="flex items-center gap-2 text-gray-600">
                          <MapPin className="w-4 h-4 text-pink-500" />
                          <span className="font-medium">{booking.hotel_name}, Room {booking.room_number}</span>
                        </div>
                        {booking.distance_km != null && (
                          <div className={`flex items-center gap-2 ${booking.distance_warning ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                            <MapPin className="w-4 h-4" />
                            {booking.distance_km}km from Sukhumvit 20
                            {booking.taxi_surcharge > 0 && (
                              <span className="font-semibold text-amber-600">
                                — Taxi: {booking.taxi_surcharge} THB
                              </span>
                            )}
                            {booking.taxi_surcharge === -1 && (
                              <span className="font-semibold text-red-600">
                                — Taxi: TBC
                              </span>
                            )}
                            {!booking.distance_warning && ' (included)'}
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-gray-600">
                          <Clock className="w-4 h-4 text-pink-500" />
                          {booking.service_type} ({booking.duration_minutes}min)
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <Phone className="w-4 h-4 text-pink-500" />
                          {booking.contact_number}
                        </div>
                        <div className="flex items-center gap-2 text-gray-600">
                          <DollarSign className="w-4 h-4 text-pink-500" />
                          ฿{booking.price_thb}
                        </div>
                      </div>

                      {/* Customer Contact Info */}
                      <div className="bg-gray-50 rounded-lg p-3 mb-4">
                        <p className="text-xs text-gray-500 mb-2">Customer Contact:</p>
                        <div className="flex flex-wrap gap-3">
                          {booking.line_id && (
                            <span className="text-sm text-green-600 flex items-center gap-1">
                              <MessageSquare className="w-4 h-4" />
                              LINE: {booking.line_id.startsWith('@') ? booking.line_id : `@${booking.line_id}`}
                            </span>
                          )}
                          {booking.whatsapp_number && (
                            <span className="text-sm text-green-600">
                              WhatsApp: {booking.whatsapp_number}
                            </span>
                          )}
                          {!booking.line_id && !booking.whatsapp_number && (
                            <span className="text-sm text-gray-500">
                              Phone only: {booking.contact_number}
                            </span>
                          )}
                        </div>
                      </div>

                      {booking.special_requests && (
                        <div className="bg-yellow-50 rounded-lg p-3 mb-4">
                          <p className="text-xs text-yellow-700 mb-1">Special Requests:</p>
                          <p className="text-sm text-yellow-800">{booking.special_requests}</p>
                        </div>
                      )}

                      {booking.booking_datetime && (
                        <div className="text-sm text-blue-600 mb-4">
                          <Calendar className="w-4 h-4 inline mr-1" />
                          Requested time: {booking.booking_datetime}
                        </div>
                      )}

                      <div className="flex gap-2">
                        {!booking.therapist_accepted && (
                          <Button
                            onClick={() => handleAcceptBooking(booking.id)}
                            disabled={actionLoading[booking.id] === 'accept'}
                            className="flex-1"
                            data-testid={`accept-btn-${booking.id}`}
                          >
                            {actionLoading[booking.id] === 'accept' ? (
                              <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                              <CheckCircle className="w-4 h-4 mr-2" />
                            )}
                            Accept Booking
                          </Button>
                        )}
                        {booking.therapist_accepted && !booking.therapist_completed && (
                          <Button
                            onClick={() => handleCompleteBooking(booking.id)}
                            disabled={actionLoading[booking.id] === 'complete'}
                            className="flex-1 bg-green-600 hover:bg-green-700"
                            data-testid={`complete-btn-${booking.id}`}
                          >
                            {actionLoading[booking.id] === 'complete' ? (
                              <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                              <CheckCircle className="w-4 h-4 mr-2" />
                            )}
                            Mark as Completed
                          </Button>
                        )}
                        <Button
                          onClick={() => setChatBooking(booking)}
                          variant="outline"
                          className="gap-1 border-pink-300 text-pink-600 hover:bg-pink-50"
                          data-testid={`chat-btn-${booking.id}`}
                        >
                          <MessageSquare className="w-4 h-4" />
                          Chat
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          <TabsContent value="history">
            <div className="space-y-4">
              {completedBookings.length === 0 ? (
                <Card>
                  <CardContent className="py-8 text-center text-gray-500">
                    No completed bookings yet
                  </CardContent>
                </Card>
              ) : (
                completedBookings.map((booking) => (
                  <Card key={booking.id} className="opacity-75">
                    <CardContent className="pt-6">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="font-semibold">{booking.name}</h3>
                          <p className="text-sm text-gray-500">{formatDate(booking.created_at)}</p>
                        </div>
                        <Badge className="bg-green-600">Completed</Badge>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          {booking.hotel_name}
                        </div>
                        <div className="flex items-center gap-2">
                          <DollarSign className="w-4 h-4" />
                          ฿{booking.price_thb}
                        </div>
                      </div>
                      {booking.therapist_completed_at && (
                        <p className="text-xs text-gray-400 mt-2">
                          Completed: {formatDate(booking.therapist_completed_at)}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Chat Dialog */}
      <TherapistChat
        booking={chatBooking}
        open={!!chatBooking}
        onClose={() => setChatBooking(null)}
        authHeaders={authHeaders}
      />
    </div>
  );
};

export default TherapistPortal;
